import { Capacitor, registerPlugin } from "@capacitor/core";

interface StoredTokens {
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number;
}

interface NativeAuthPlugin {
    authenticate(options: { url: string; callbackScheme: string }): Promise<{ callbackUrl: string }>;
    getTokens(): Promise<StoredTokens>;
    setTokens(tokens: Required<Pick<StoredTokens, "accessToken">> & StoredTokens): Promise<void>;
    clearTokens(): Promise<void>;
}

const NativeAuth = registerPlugin<NativeAuthPlugin>("NativeAuth");

const oauth = {
    authorizeUrl: import.meta.env.VITE_NATIVE_OAUTH_AUTHORIZE_URL as string | undefined,
    tokenUrl: import.meta.env.VITE_NATIVE_OAUTH_TOKEN_URL as string | undefined,
    userinfoUrl: import.meta.env.VITE_NATIVE_OAUTH_USERINFO_URL as string | undefined,
    clientId: import.meta.env.VITE_NATIVE_OAUTH_CLIENT_ID as string | undefined,
    redirectUri: (import.meta.env.VITE_NATIVE_OAUTH_REDIRECT_URI as string | undefined)
        ?? "liveroomos://oauth/callback",
    scope: (import.meta.env.VITE_NATIVE_OAUTH_SCOPE as string | undefined)
        ?? "openid profile email offline_access",
    resource: import.meta.env.VITE_NATIVE_OAUTH_RESOURCE as string | undefined,
};

let refreshInFlight: Promise<string | null> | null = null;

export const NATIVE_AUTH_REQUIRED_EVENT = "kardoe-os:native-auth-required";

export function isNativeIOS(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export function isNativeAuthConfigured(): boolean {
    return Boolean(oauth.authorizeUrl && oauth.tokenUrl && oauth.clientId && oauth.redirectUri);
}

function base64Url(bytes: Uint8Array): string {
    let raw = "";
    for (const byte of bytes) raw += String.fromCharCode(byte);
    return btoa(raw).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function randomUrlSafe(byteLength = 32): string {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return base64Url(bytes);
}

async function sha256UrlSafe(value: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return base64Url(new Uint8Array(digest));
}

function callbackScheme(): string {
    const scheme = new URL(oauth.redirectUri).protocol.replace(/:$/, "");
    if (!scheme) throw new Error("Native OAuth redirect URI needs a custom URL scheme.");
    return scheme;
}

async function exchange(body: URLSearchParams): Promise<StoredTokens> {
    if (!oauth.tokenUrl || !oauth.clientId) throw new Error("Native OAuth is not configured.");
    body.set("client_id", oauth.clientId);
    if (oauth.resource) body.set("resource", oauth.resource);

    const response = await fetch(oauth.tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
        credentials: "omit",
    });
    const payload = await response.json() as {
        access_token?: unknown;
        refresh_token?: unknown;
        expires_in?: unknown;
        error?: unknown;
        error_description?: unknown;
    };
    if (!response.ok || typeof payload.access_token !== "string") {
        const detail = typeof payload.error_description === "string"
            ? payload.error_description
            : typeof payload.error === "string" ? payload.error : `HTTP ${response.status}`;
        throw new Error(`OAuth token exchange failed: ${detail}`);
    }

    return {
        accessToken: payload.access_token,
        refreshToken: typeof payload.refresh_token === "string" ? payload.refresh_token : undefined,
        expiresAt: typeof payload.expires_in === "number"
            ? Date.now() + Math.max(0, payload.expires_in - 30) * 1_000
            : undefined,
    };
}

export async function signInNative(): Promise<void> {
    if (!isNativeIOS()) return;
    if (!isNativeAuthConfigured()) {
        throw new Error("Set the VITE_NATIVE_OAUTH_* values before building the iOS app.");
    }

    const state = randomUrlSafe();
    const verifier = randomUrlSafe(64);
    const challenge = await sha256UrlSafe(verifier);
    const authorizationUrl = new URL(oauth.authorizeUrl!);
    authorizationUrl.searchParams.set("response_type", "code");
    authorizationUrl.searchParams.set("client_id", oauth.clientId!);
    authorizationUrl.searchParams.set("redirect_uri", oauth.redirectUri);
    authorizationUrl.searchParams.set("scope", oauth.scope);
    authorizationUrl.searchParams.set("state", state);
    authorizationUrl.searchParams.set("code_challenge", challenge);
    authorizationUrl.searchParams.set("code_challenge_method", "S256");

    const { callbackUrl } = await NativeAuth.authenticate({
        url: authorizationUrl.toString(),
        callbackScheme: callbackScheme(),
    });
    const callback = new URL(callbackUrl);
    if (callback.searchParams.get("state") !== state) throw new Error("OAuth state did not match.");
    const authorizationError = callback.searchParams.get("error");
    if (authorizationError) throw new Error(`OAuth authorization failed: ${authorizationError}`);
    const code = callback.searchParams.get("code");
    if (!code) throw new Error("OAuth callback did not include an authorization code.");

    const tokens = await exchange(new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: oauth.redirectUri,
        code_verifier: verifier,
    }));
    await NativeAuth.setTokens({ ...tokens, accessToken: tokens.accessToken! });
}

async function readFreshAccessToken(): Promise<string | null> {
    if (!isNativeIOS()) return null;
    const stored = await NativeAuth.getTokens();
    if (!stored.accessToken) return null;
    if (!stored.expiresAt || stored.expiresAt > Date.now()) return stored.accessToken;
    if (!stored.refreshToken) {
        await NativeAuth.clearTokens();
        return null;
    }

    const refreshed = await exchange(new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: stored.refreshToken,
    }));
    const next = {
        ...refreshed,
        accessToken: refreshed.accessToken!,
        refreshToken: refreshed.refreshToken ?? stored.refreshToken,
    };
    await NativeAuth.setTokens(next);
    return next.accessToken;
}

export async function getNativeAccessToken(): Promise<string | null> {
    if (!refreshInFlight) {
        refreshInFlight = readFreshAccessToken().finally(() => { refreshInFlight = null; });
    }
    return refreshInFlight;
}

export async function getNativeUserInfo(): Promise<{ sub: string } | null> {
    if (!isNativeIOS() || !oauth.userinfoUrl) return null;
    const accessToken = await getNativeAccessToken();
    if (!accessToken) return null;

    const response = await fetch(oauth.userinfoUrl, {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "omit",
    });
    if (!response.ok) {
        if (response.status === 401) await NativeAuth.clearTokens();
        throw new Error(`Native session check failed: HTTP ${response.status}`);
    }
    const payload = await response.json() as { sub?: unknown };
    return typeof payload.sub === "string" ? { sub: payload.sub } : null;
}

export async function clearNativeSession(): Promise<void> {
    if (isNativeIOS()) await NativeAuth.clearTokens();
}

/** Adds the Keychain-backed bearer token for native calls; browsers keep cookie auth. */
export async function authenticatedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers);
    const token = await getNativeAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(input, {
        ...init,
        headers,
        credentials: isNativeIOS() ? "omit" : (init.credentials ?? "include"),
    });
}
